import { useState } from 'react';
import {
    Grid,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    FormGroup,
    InputLabel,
    Select,
    FormControl,
    Button,
    FormHelperText,
    Typography,
    Card,
    CardContent,
    Divider,
    Box
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useTranslation } from 'react-i18next';

const AEROBIC_TYPES = [
    'rehabilitation',
    'treadmill',
    'ergometer',
    'walking',
    'running',
    'cycling',
    'rest',
    'other'
];

const RESISTANCE_TYPES = [
    'squat',
    'sit_to_stand',
    'push',
    'pull',
    'arm_raise',
    'knee_extension'
];

const ExerciseBuilder = ({ onAdd, hidePeriod = false }) => {
    const [type, setType] = useState('');
    const [exerciseKind, setExerciseKind] = useState('aerobic');
    const [description, setDescription] = useState('');
    const [duration, setDuration] = useState('');
    const [sets, setSets] = useState('');
    const [reps, setReps] = useState('');
    const [hour, setHour] = useState('');
    const [minute, setMinute] = useState('');
    const [days, setDays] = useState([]);
    const [errors, setErrors] = useState({});
    const { t } = useTranslation();

    const TYPE_LABELS = {
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        other: t('exercise.types.other')
    };

    const RESISTANCE_LABELS = {
        squat: t('exercise.types.squat'),
        sit_to_stand: t('exercise.types.sit_to_stand'),
        push: t('exercise.types.push'),
        pull: t('exercise.types.pull'),
        arm_raise: t('exercise.types.arm_raise'),
        knee_extension: t('exercise.types.knee_extension')
    };

    const DAYS = [
        { label: t('session.days.sunday'), value: 0 },
        { label: t('session.days.monday'), value: 1 },
        { label: t('session.days.tuesday'), value: 2 },
        { label: t('session.days.wednesday'), value: 3 },
        { label: t('session.days.thursday'), value: 4 },
        { label: t('session.days.friday'), value: 5 },
        { label: t('session.days.saturday'), value: 6 }
    ];

    const toggleDay = (day) => {
        setDays((prev) =>
            prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
        );
    };

    const handleAdd = () => {
        let newErrors = {};

        if (!type) newErrors.type = t('errors.exercise_type');

        if (exerciseKind === 'aerobic') {
            if (!duration) newErrors.duration = t('errors.exercise_duration');
        } else {
            if (!sets) newErrors.sets = t('errors.exercise_sets');
            if (!reps) newErrors.reps = t('errors.exercise_reps');
        }

        if (!hidePeriod && days.length === 0) newErrors.days = t('errors.select_days');

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        let period = null;
        if (!hidePeriod) {
            const finalHour = hour ? hour : 0;
            const finalMinute = minute ? minute : 0;
            period = `${finalMinute} ${finalHour} * * ${days.sort().join(',')}`;
        }

        const baseExercise = {
            id: Date.now(),
            type,
            description,
            order: 0
        };

        const exercise =
            exerciseKind === 'aerobic'
                ? {
                      ...baseExercise,
                      duration: parseInt(duration) * 60
                  }
                : {
                      ...baseExercise,
                      sets: parseInt(sets),
                      reps: parseInt(reps)
                  };

        if (period) exercise.period = period;

        onAdd(exercise);

        setType('');
        setDescription('');
        setDuration('');
        setHour('');
        setMinute('');
        setDays([]);
        setErrors({});
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                boxShadow: 1,
                p: 2,
                backgroundColor: 'background.paper'
            }}
        >
            <CardContent>
                <Grid container spacing={2}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {hidePeriod ? t('exercise.add_to_session') : t('exercise.create')}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('exercise.kind')}</InputLabel>
                                <Select
                                    value={exerciseKind}
                                    label={t('exercise.kind')}
                                    onChange={(e) => {
                                        setExerciseKind(e.target.value);
                                        setType('');
                                        setErrors({});
                                    }}
                                >
                                    <MenuItem value="aerobic">
                                        {t('exercise.kinds.aerobic')}
                                    </MenuItem>
                                    <MenuItem value="resistance">
                                        {t('exercise.kinds.resistance')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={!!errors.type}>
                                <InputLabel>{t('exercise.exercice_name')}</InputLabel>
                                <Select
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    label={t('exercise.exercice_name')}
                                >
                                    {(exerciseKind === 'aerobic'
                                        ? AEROBIC_TYPES
                                        : RESISTANCE_TYPES
                                    ).map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {exerciseKind === 'aerobic'
                                                ? TYPE_LABELS[opt]
                                                : RESISTANCE_LABELS[opt]}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.type && (
                                    <FormHelperText>{errors.type}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>

                    {exerciseKind === 'aerobic' && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('exercise.duration_label')}
                                    type="number"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    error={!!errors.duration}
                                    helperText={errors.duration}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {exerciseKind === 'resistance' && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('exercise.sets')}
                                    type="number"
                                    value={sets}
                                    onChange={(e) => setSets(e.target.value)}
                                    error={!!errors.sets}
                                    helperText={errors.sets}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('exercise.reps')}
                                    type="number"
                                    value={reps}
                                    onChange={(e) => setReps(e.target.value)}
                                    error={!!errors.reps}
                                    helperText={errors.reps}
                                />
                            </Grid>
                        </Grid>
                    )}

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('exercise.description_label')}
                                multiline
                                minRows={2}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    {!hidePeriod && (
                        <>
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    gutterBottom
                                >
                                    {t('exercise.schedule_title')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('session.hour_label')}
                                    type="number"
                                    value={hour}
                                    onChange={(e) => setHour(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('session.minute_label')}
                                    type="number"
                                    value={minute}
                                    onChange={(e) => setMinute(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormGroup row>
                                    {DAYS.map((d) => (
                                        <FormControlLabel
                                            key={d.value}
                                            control={
                                                <Checkbox
                                                    checked={days.includes(d.value)}
                                                    onChange={() => toggleDay(d.value)}
                                                />
                                            }
                                            label={d.label}
                                        />
                                    ))}
                                </FormGroup>
                                {errors.days && (
                                    <FormHelperText error>{errors.days}</FormHelperText>
                                )}
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={handleAdd}
                            >
                                {t('exercise.confirm_button')}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default ExerciseBuilder;
